<template>
  <v-row>
    <v-col cols="12" sm="6">
      <div>
        <span class="me-2">{{ $t("exchange.canUse") }}</span>
        <span class="me-2">{{
          $thousandMask(wallet.base, currentCoin.frontBaseCoinScale)
        }}</span>
        <span class="me-2">{{ currentCoin.baseSymbol }}</span>
        <a
          v-if="currentCoin.coinType !== 'VIRTUAL'"
          :href="
            `${baseUrl}/operations/transfer?from=${currentCoin.baseSymbol}`
          "
          class="text-yellow"
        >
          {{ $t("exchange.balanceIncrease") }}
        </a>
      </div>
      <label>{{ $t("exchange.limited_price") }}</label>
      <v-text-field
        v-model="buy.ocoLimitPrice"
        placeholder="0"
        outlined
        color="primaryX"
        background-color="var(--v-field-base)"
        @input="handlePrice('buy', 'ocoLimitPrice')"
        @keyup="checkPriceRules('buy', 'ocoLimitPrice')"
      >
        <template v-slot:prepend-inner>
          <p class="me-6 mb-0 mt-1">{{ currentCoin.baseSymbol }}</p>
        </template>
        <template v-slot:append>
          <p class="mb-0 mt-1">{{ $t("exchange.limitOrderPrice") }}</p>
        </template>
      </v-text-field>
      <label>{{ $t("exchange.stop_order_price") }}</label>
      <v-text-field
        v-model="buy.ocoActivationPrice"
        placeholder="0"
        outlined
        color="primaryX"
        background-color="var(--v-field-base)"
        @input="handlePrice('buy', 'ocoActivationPrice')"
        @keyup="checkPriceRules('buy', 'ocoActivationPrice')"
      >
        <template v-slot:prepend-inner>
          <p class="me-6 mb-0 mt-1">{{ currentCoin.baseSymbol }}</p>
        </template>
        <template v-slot:append>
          <p class="mb-0 mt-1">{{ $t("exchange.activationPrice") }}</p>
        </template>
      </v-text-field>
      <v-text-field
        v-model="buy.ocoConditionalOrderPrice"
        placeholder="0"
        outlined
        color="primaryX"
        background-color="var(--v-field-base)"
        @input="
          setAmount('buy', 'ocoConditionalOrderPrice', 'frontBaseCoinScale')
        "
        @keyup="checkRules('buy', 'ocoConditionalOrderPrice')"
      >
        <template v-slot:prepend-inner>
          <p class="me-6 mb-0 mt-1">{{ currentCoin.baseSymbol }}</p>
        </template>
        <template v-slot:append>
          <p class="mb-0 mt-1">{{ $t("exchange.buyPrice") }}</p>
        </template>
      </v-text-field>
      <v-text-field
        v-model="buy.ocoAmount"
        placeholder="0"
        outlined
        color="primaryX"
        background-color="var(--v-field-base)"
        @input="setAmount('buy', 'ocoAmount', 'frontCoinScale')"
        @keyup="checkRules('buy', 'ocoAmount')"
      >
        <template v-slot:prepend-inner>
          <p class="me-6 mb-0 mt-1">{{ currentCoin.coinSymbol }}</p>
        </template>
        <template v-slot:append>
          <p class="mb-0 mt-1">{{ $t("exchange.buyAmount") }}</p>
        </template>
      </v-text-field>
      <v-slider
        class="ltr"
        color="green"
        v-model="buy.sliderPercent"
        @change="handleBuySlider"
        thumb-label="always"
        track-color="none"
        thumb-color="none"
      >
        <template v-slot:thumb-label="{ value }">
          <span class="font-Regular-12 green--text"
            >{{ value }}%</span
          ></template
        >
      </v-slider>

      <div
        class="d-flex justify-space-between align-center font-Regular-12 gray2--text mb-8"
      >
        <p>{{ $t("exchange.estimatedReceipt") }}</p>
        <p class="ltr">
          <img alt="" class="mr-2" src="@/assets/image/nearAmount.svg" />
          <span>
            {{
              $thousandMask(
                buy.ocoAmount.toString().replace(/,/gi, "") -
                  buy.ocoAmount.toString().replace(/,/gi, "") *
                    currentCoin.takerFee,
                currentCoin.frontCoinScale
              )
            }}
          </span>
          <span>{{ currentCoin.coinSymbol }}</span>
        </p>
      </div>
      <v-btn
        block
        color="green"
        :loading="loadBuy"
        @click="buyCrypto"
        :disabled="disableBuy"
      >
        {{ $t("exchange.buy") }} ({{ currentCoin.coinSymbol }})
      </v-btn>
    </v-col>
    <v-col cols="12" sm="6">
      <div>
        <span class="me-2">{{ $t("exchange.canUse") }}</span>
        <span class="me-2">{{
          $thousandMask(wallet.coin, currentCoin.frontCoinScale)
        }}</span>
        <span class="me-2">{{ currentCoin.coinSymbol }}</span>
        <a
          v-if="currentCoin.coinType !== 'VIRTUAL'"
          :href="
            `${baseUrl}/operations/transfer?from=${currentCoin.coinSymbol}`
          "
          class="text-yellow"
        >
          {{ $t("exchange.balanceIncrease") }}
        </a>
      </div>
      <label>{{ $t("exchange.limited_price") }}</label>
      <v-text-field
        v-model="sell.ocoLimitPrice"
        placeholder="0"
        outlined
        color="primaryX"
        background-color="var(--v-field-base)"
        @input="handlePrice('sell', 'ocoLimitPrice')"
        @keyup="checkPriceRules('sell', 'ocoLimitPrice')"
      >
        <template v-slot:prepend-inner>
          <p class="me-6 mb-0 mt-1">{{ currentCoin.baseSymbol }}</p>
        </template>
        <template v-slot:append>
          <p class="mb-0 mt-1">{{ $t("exchange.limitOrderPrice") }}</p>
        </template>
      </v-text-field>
      <label>{{ $t("exchange.stop_order_price") }}</label>
      <v-text-field
        v-model="sell.ocoActivationPrice"
        placeholder="0"
        outlined
        color="primaryX"
        background-color="var(--v-field-base)"
        @input="handlePrice('sell')"
        @keyup="checkPriceRules('sell')"
      >
        <template v-slot:prepend-inner>
          <p class="me-6 mb-0 mt-1">{{ currentCoin.baseSymbol }}</p>
        </template>
        <template v-slot:append>
          <p class="mb-0 mt-1">{{ $t("exchange.activationPrice") }}</p>
        </template>
      </v-text-field>
      <v-text-field
        v-model="sell.ocoConditionalOrderPrice"
        placeholder="0"
        outlined
        color="primaryX"
        background-color="var(--v-field-base)"
        @input="
          setAmount('sell', 'ocoConditionalOrderPrice', 'frontBaseCoinScale')
        "
        @keyup="checkRules('sell', 'ocoConditionalOrderPrice')"
      >
        <template v-slot:prepend-inner>
          <p class="me-6 mb-0 mt-1">{{ currentCoin.baseSymbol }}</p>
        </template>
        <template v-slot:append>
          <p class="mb-0 mt-1">{{ $t("exchange.sellPrice") }}</p>
        </template>
      </v-text-field>
      <v-text-field
        v-model="sell.ocoAmount"
        placeholder="0"
        outlined
        color="primaryX"
        background-color="var(--v-field-base)"
        @input="setAmount('sell', 'ocoAmount', 'frontCoinScale')"
        @keyup="checkRules('sell', 'ocoAmount')"
      >
        <template v-slot:prepend-inner>
          <p class="me-6 mb-0 mt-1">{{ currentCoin.coinSymbol }}</p>
        </template>
        <template v-slot:append>
          <p class="mb-0 mt-1">{{ $t("exchange.sellAmount") }}</p>
        </template>
      </v-text-field>
      <v-slider
        class="ltr"
        color="red"
        v-model="sell.sliderPercent"
        @change="handleSellSlider"
        thumb-label="always"
        thumb-color="none"
        track-color="none"
      >
        <template v-slot:thumb-label="{ value }">
          <span class="font-Regular-12 red--text">{{ value }}%</span></template
        >
      </v-slider>

      <div
        class="d-flex justify-space-between align-center font-Regular-12 gray2--text mb-8"
      >
        <p>{{ $t("exchange.estimatedReceipt") }}</p>
        <p class="ltr">
          <img alt="" class="mr-2" src="@/assets/image/nearAmount.svg" />
          <span>
            {{
              $thousandMask(
                (sell.ocoAmount.toString().replace(/,/gi, "") -
                  sell.ocoAmount.toString().replace(/,/gi, "") *
                    currentCoin.takerFee) *
                  sell.ocoConditionalOrderPrice.toString().replace(/,/gi, ""),
                currentCoin.frontBaseCoinScale
              )
            }}
          </span>
          <span>{{ currentCoin.baseSymbol }}</span>
        </p>
      </div>
      <v-btn
        block
        color="red"
        :loading="loadSell"
        @click="sellCrypto"
        :disabled="disableSell"
      >
        {{ $t("exchange.sell") }} ({{ currentCoin.coinSymbol }})
      </v-btn>
    </v-col>
  </v-row>
</template>
<script>
import $thousandMask from "@/mixins/thousandMask";
import { ExchangeService } from "@/services";
export default {
  name: "ocoOrder",
  mixins: [$thousandMask],
  props: {
    wallet: {
      type: Object,
      required: true
    },
    currentCoin: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      baseUrl: process.env.VUE_APP_BASE_URL,
      buy: {
        ocoLimitPrice: "",
        ocoActivationPrice: "",
        ocoConditionalOrderPrice: "",
        ocoAmount: "",
        ocoTurnover: 0.0,
        sliderPercent: 0
      },
      sell: {
        ocoLimitPrice: "",
        ocoActivationPrice: "",
        ocoConditionalOrderPrice: "",
        ocoAmount: "",
        ocoTurnover: 0.0,
        sliderPercent: 0
      },
      loadBuy: false,
      loadSell: false
    };
  },
  computed: {
    disableBuy() {
      if (this.isAdmin) return false;
      else {
        return (
          !this.currentCoin.enableOco ||
          this.currentCoin.exchangeable === 2 ||
          !this.currentCoin
        );
      }
    },
    disableSell() {
      if (this.isAdmin) return false;
      else {
        return (
          !this.currentCoin.enableOco ||
          this.currentCoin.exchangeable === 2 ||
          !this.currentCoin
        );
      }
    }
  },
  mounted() {
    this.buy.ocoConditionalOrderPrice = this.$thousandMask(
      this.currentCoin.close,
      this.currentCoin.frontBaseCoinScale
    );
    this.sell.ocoConditionalOrderPrice = this.$thousandMask(
      this.currentCoin.close,
      this.currentCoin.frontBaseCoinScale
    );
  },
  methods: {
    checkPriceRules(obj, prop) {
      this[obj][prop] = this[obj][prop].replace(/[^\d.,]/g, "");
    },
    handlePrice(obj, prop) {
      if (!this[obj][prop]) return;
      const str = this[obj][prop].toString();
      let number = str.replace(/,/gi, "");
      this[obj][prop] = this.$thousandMask(
        number,
        this.currentCoin.frontBaseCoinScale
      );
    },
    checkRules(obj, prop) {
      if (this[obj][prop] === "") {
        this.setSliderPercent(obj);
      } else {
        this[obj][prop] = this[obj][prop].replace(/[^\d.,]/g, "");
      }
    },
    setAmount(obj, prop, scale) {
      if (!this[obj][prop]) return;
      const str = this[obj][prop].toString();
      let number = str.replace(/,/gi, "");
      const maskedValue = this.$thousandMask(number, this.currentCoin[scale]);

      // Force update using nextTick
      this.$nextTick(() => {
        this[obj][prop] = maskedValue;
        this[obj].ocoTurnover = this.toFloor(
          this.mul(this[obj].ocoConditionalOrderPrice, this[obj].ocoAmount)
        );
        this.setSliderPercent(obj);
      });
    },
    setSliderPercent(obj) {
      let amount = this[obj].ocoAmount.replace(/,/gi, "");
      let price = this[obj].ocoConditionalOrderPrice.replace(/,/gi, "");
      let account = this.wallet.coin;
      let turnOver = amount;
      if (obj === "buy") {
        account = this.wallet.base;
        turnOver = this.mul(price, amount);
      }
      this[obj].sliderPercent =
        account > 0 && parseFloat(price) > 0 && parseFloat(amount) > 0
          ? parseFloat(this.mul(this.div(turnOver, account), 100))
          : 0;
    },
    handleBuySlider(val) {
      let price = this.buy.ocoConditionalOrderPrice.replace(/,/gi, ""),
        account = this.wallet.base,
        amount = 0;
      if (price > 0) {
        amount = this.mul(this.mul(this.div(account, price), val), 0.01);
      }
      this.buy.ocoAmount = amount;
      this.buy.ocoTurnover = this.toFloor(this.mul(this.buy.ocoAmount, price));
      this.setAmount("buy", "ocoAmount", "frontCoinScale");
    },
    handleSellSlider(val) {
      let price = this.sell.ocoConditionalOrderPrice.replace(/,/gi, "");
      this.sell.ocoAmount = this.mul(this.mul(this.wallet.coin, val), 0.01);
      this.sell.ocoTurnover = this.toFloor(this.mul(this.buy.ocoAmount, price));
      this.setAmount("sell", "ocoAmount", "frontCoinScale");
    },
    setPLateData(price) {
      this.buy.ocoConditionalOrderPrice = this.$thousandMask(
        price,
        this.currentCoin.frontBaseCoinScale
      );
      this.sell.ocoConditionalOrderPrice = this.$thousandMask(
        price,
        this.currentCoin.frontBaseCoinScale
      );

      this.buy.ocoAmount = "";
      this.setSliderPercent("buy");
      this.sell.ocoAmount = "";
      this.setSliderPercent("sell");
    },
    buyCrypto() {
      const ocoLimitPrice = this.buy.ocoLimitPrice.replace(/,/gi, "");
      const ocoActivationPrice = this.buy.ocoActivationPrice.replace(/,/gi, "");
      const ocoConditionalOrderPrice = this.buy.ocoConditionalOrderPrice.replace(
        /,/gi,
        ""
      );
      const ocoAmount = this.buy.ocoAmount.replace(/,/gi, "");
      if (!ocoLimitPrice || ocoLimitPrice === "0") {
        this.$toast.error(this.$t("exchange.enterLimitOrderPrice"));
        return;
      }
      if (!ocoActivationPrice || ocoActivationPrice === "0") {
        this.$toast.error(this.$t("exchange.enterActivationPrice"));
        return;
      }
      if (!ocoConditionalOrderPrice || ocoConditionalOrderPrice === "0") {
        this.$toast.error(this.$t("exchange.enterBuyPrice"));
        return;
      }
      if (!ocoAmount || ocoAmount === "0") {
        this.$toast.error(this.$t("exchange.enterBuyAmount"));
        return;
      }
      if (
        parseFloat(ocoAmount) >
        this.wallet.base / parseFloat(ocoLimitPrice)
      ) {
        this.$toast.error(this.$t("exchange.insufficientBalance"));
        return;
      }
      this.loadBuy = true;
      let payload = {
        activationPrice: ocoActivationPrice,
        amount: ocoAmount,
        conditionalOrderPrice: ocoConditionalOrderPrice,
        direction: "BUY",
        limitOrderPrice: ocoLimitPrice,
        symbol: this.currentCoin.symbol
      };
      ExchangeService.addOCOOrder(payload)
        .then(res => {
          if (res.data.code !== 500) {
            this.$toast.success(this.$t("exchange.success"));
          }
          this.$emit("refresh");
          this.buy.ocoAmount = "";
          this.buy.sliderPercent = 0;
        })
        .catch(err => {
          this.$toast.error(err.message);
        })
        .finally(() => {
          this.loadBuy = false;
        });
    },
    sellCrypto() {
      const ocoLimitPrice = this.sell.ocoLimitPrice.replace(/,/gi, "");
      const ocoActivationPrice = this.sell.ocoActivationPrice.replace(
        /,/gi,
        ""
      );
      const ocoConditionalOrderPrice = this.sell.ocoConditionalOrderPrice.replace(
        /,/gi,
        ""
      );
      const ocoAmount = this.sell.ocoAmount.replace(/,/gi, "");
      if (!ocoLimitPrice || ocoLimitPrice === "0") {
        this.$toast.error(this.$t("exchange.enterLimitOrderPrice"));
        return;
      }
      if (!ocoActivationPrice || ocoActivationPrice === "0") {
        this.$toast.error(this.$t("exchange.enterActivationPrice"));
        return;
      }
      if (!ocoConditionalOrderPrice || ocoConditionalOrderPrice === "0") {
        this.$toast.error(this.$t("exchange.enterSellPrice"));
        return;
      }
      if (!ocoAmount || ocoAmount === "0") {
        this.$toast.error(this.$t("exchange.enterSellAmount"));
        return;
      }
      if (parseFloat(ocoAmount) > parseFloat(this.wallet.coin)) {
        this.$toast.error(this.$t("exchange.insufficientBalance"));
        return;
      }
      this.loadSell = true;
      let payload = {
        activationPrice: ocoActivationPrice,
        amount: ocoAmount,
        conditionalOrderPrice: ocoConditionalOrderPrice,
        direction: "SELL",
        limitOrderPrice: ocoLimitPrice,
        symbol: this.currentCoin.symbol
      };
      ExchangeService.addOCOOrder(payload)
        .then(res => {
          if (res.data.code !== 500) {
            this.$toast.success(this.$t("exchange.success"));
          }
          this.$emit("refresh");
          this.sell.ocoAmount = "";
          this.sell.sliderPercent = 0;
        })
        .catch(err => {
          this.$toast.error(err.message);
        })
        .finally(() => {
          this.loadSell = false;
        });
    },
    //// helping methods
    mul(a, b) {
      let c = 0,
        d = a.toString(),
        e = b.toString();
      if (d.split(".")[1]) c += d.split(".")[1].length;
      if (e.split(".")[1]) c += e.split(".")[1].length;
      return (
        (Number(d.replace(".", "")) * Number(e.replace(".", ""))) /
        Math.pow(10, c)
      );
    },
    div(arg1, arg2) {
      let t1 = 0,
        t2 = 0,
        r1,
        r2;
      if (arg1.toString().split(".")[1])
        t1 = arg1.toString().split(".")[1].length;
      if (arg2.toString().split(".")[1])
        t2 = arg2.toString().split(".")[1].length;

      r1 = Number(arg1.toString().replace(".", ""));
      r2 = Number(arg2.toString().replace(".", ""));
      return (r1 / r2) * Math.pow(10, t2 - t1);
    },
    toFloor(number, scale = 8) {
      if (Number(number) === 0) {
        return 0;
      }
      let __str = number + "";
      if (__str.indexOf("e") > -1 || __str.indexOf("E") > -1) {
        let __num = Number(number).toFixed(scale + 1),
          __str = __num + "";
        return __str.substring(0, __str.length - 1);
      } else if (__str.indexOf(".") > -1) {
        if (scale === 0) {
          return __str.substring(0, __str.indexOf("."));
        }
        return __str.substring(0, __str.indexOf(".") + scale + 1);
      } else {
        return __str;
      }
    }
  }
};
</script>
<style scoped lang="scss"></style>
